import React from "react";
import { Box, Typography, alpha, useTheme } from "@mui/material";
import type { FatigueRiskTier } from "../data/types";
import { tierColor, tierLabel } from "./primitives";

interface RiskTierBadgeProps {
  tier: FatigueRiskTier;
  /** Optional descriptive suffix, e.g. "Act now". */
  caption?: string;
  size?: "small" | "medium";
}

/** Pill badge for a RAG risk tier, used in tables, cards and headers. */
export const RiskTierBadge: React.FC<RiskTierBadgeProps> = ({
  tier,
  caption,
  size = "medium",
}) => {
  const theme = useTheme();
  const color = tierColor(tier, theme);
  const label = caption ?? tierLabel(tier);
  return (
    <Box
      role="status"
      aria-label={`Risk tier ${tierLabel(tier)}${caption ? `: ${caption}` : ""}`}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.75,
        px: size === "small" ? 1 : 1.25,
        py: size === "small" ? 0.25 : 0.5,
        borderRadius: 999,
        bgcolor: alpha(color, 0.14),
        border: `1px solid ${alpha(color, 0.4)}`,
      }}
    >
      <Box
        sx={{
          width: 8,
          height: 8,
          borderRadius: "50%",
          bgcolor: color,
          boxShadow: `0 0 6px ${alpha(color, 0.8)}`,
        }}
      />
      <Typography
        variant="caption"
        sx={{ fontWeight: 700, color, fontSize: size === "small" ? 11 : 12 }}
      >
        {label}
      </Typography>
    </Box>
  );
};
